/**
 * The faces question, as Import Annual Reports and Import Newsletters both ask it.
 *
 * A PDF on either shelf is readable by supporters only if somebody states that the faces
 * in it are already covered, and nothing in the archive can check that statement (see
 * the head of either page). So the two pages must ask it in the same words, in the same
 * two places — inside the dropzone, before a file is chosen, and beside the Shelve
 * button, the last moment anything can change. Both used to carry their own copy of
 * every sentence here; what differs between them is only what a file is called.
 *
 * `item` is one file on the shelf ('report', 'issue'); `kind` is what the shelf holds
 * ('report', 'newsletter').
 */
import { Checkbox, Notice } from './ui'
import { Icon } from './Icon'

/** Said inside the dropzone: what a file has to be before it is chosen. */
export const FacesWarning = () => (
  <>
    <strong>
      <Icon name="warning" size={14} /> Blur or block out the faces before you upload.
    </strong>
    This page does not do it for you and cannot tell whether it was done. Each PDF is
    stored exactly as you hand it over and, once you confirm the faces are covered, it is
    the file every donor is given to read.
  </>
)

/**
 * Who will be able to read what is about to be shelved, and the tick that decides it.
 *
 * The tick is never carried over by the pages between batches — whether the faces are
 * covered is a fact about the files in front of you — so it is held by the page, not here.
 */
export function WhoCanRead({ covered, onCovered, count, item, kind }) {
  const these = count === 1 ? `This ${item}` : `These ${count} ${item}s`
  return (
    <>
      <Notice
        tone={covered ? 'ok' : 'warn'}
        icon={covered ? 'eye' : 'lock'}
        title={
          covered
            ? `Supporters will be able to read these ${kind}s`
            : `Supporters will not be able to open these ${kind}s`
        }
      >
        {covered ? (
          <>
            {these} will be stored as {count === 1 ? 'it stands' : 'they stand'} and served
            to every account the archive lets in, including every donor. That is on the
            strength of the box below — nothing here has checked the{' '}
            {count === 1 ? 'file' : 'files'}.
          </>
        ) : (
          <>
            {these} will be shelved for staff only. No view-only account and no donor will
            be able to open {count === 1 ? 'it' : 'them'}; they are told the {kind} is
            unavailable. Tick below if the faces are already covered.
          </>
        )}
      </Notice>
      <Checkbox
        checked={covered}
        onChange={onCovered}
        label="Every face in these files is already blurred or blocked out — supporters may read them"
      />
    </>
  )
}
